import * as React from 'react';
import { Avatar, Box, Stack, Typography, useTheme, Breadcrumbs, Link } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@/hooks/v2/use-user';
import { paths } from '@/paths';

interface CommunityPageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  showLogo?: boolean;
  icon?: React.ReactNode;
  breadcrumbs?: {
    label: string;
    href?: string;
    onClick?: () => void;
  }[];
}

export function CommunityPageHeader({
  title,
  subtitle,
  actions,
  showLogo = true,
  icon,
  breadcrumbs,
}: CommunityPageHeaderProps): React.JSX.Element {
  const theme = useTheme();
  const { community } = useUser();
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 4, mt: 4 }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 ? <Breadcrumbs
          separator={<ChevronRightIcon fontSize="small" />}
          sx={{ 
            mb: 3,
            '& .MuiBreadcrumbs-separator': {
              color: 'text.secondary',
            }
          }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <Link
              component="button"
              key={index}
              onClick={() => {
                if (breadcrumb.onClick) {
                  breadcrumb.onClick();
                } else if (breadcrumb.href) {
                  navigate(breadcrumb.href);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                color: index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary',
                fontWeight: index === breadcrumbs.length - 1 ? 500 : 400,
                '&:hover': {
                  color: 'primary.main',
                }
              }}
            >
              {breadcrumb.label}
            </Link>
          ))}
        </Breadcrumbs> : null}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          {showLogo ? icon ? (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  border: '2px solid',
                  borderColor: 'primary.light',
                  boxShadow: (t) => `0 8px 32px ${t.palette.primary.main}20`,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            ) : (
              <Avatar
                src={(community as any)?.logo_url}
                sx={{ '--Avatar-size': '64px', width: 64, height: 64 }}
                variant="rounded"
              >
                {community?.name
                  ? community.name
                      .split(' ')
                      .slice(0, 2)
                      .map((part) => part.charAt(0))
                      .join('')
                  : ''}
              </Avatar>
            ) : null}
          <Box>
            <Typography sx={{ fontWeight: 600, color: theme.palette.text.primary }} variant="h4">
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ color: theme.palette.text.secondary }} variant="body1">
                {subtitle}
              </Typography>
            ) : null}
            {community?.name ? <Typography sx={{ color: theme.palette.text.secondary, mt: 0.5 }} variant="body2">
                {community.name}
                {community?.address ? ` • ${community.address}` : ''}
                {community?.city || community?.state || community?.zip_code
                  ? ` • ${community?.city ?? ''}${community?.city && community?.state ? ', ' : ''}${
                      community?.state ?? ''
                    } ${community?.zip_code ?? ''}`.trim()
                  : ''}
              </Typography> : null}
          </Box>
        </Stack>
        {actions ? <Box sx={{ ml: 2 }}>{actions}</Box> : null}
      </Stack>
    </Box>
  );
}


